import { useCallback, useEffect, useMemo, useState } from 'react';

import CloseFullscreen from '@mui/icons-material/CloseFullscreen';
import OpenInFull from '@mui/icons-material/OpenInFull';
import { enqueueSnackbar } from 'notistack';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag } from '@/services/feature-flags/react';
import { Box, Paper } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import AddToDashboardButton from '@/features/markets/components/AddToDashboardButton';
import { hasOtherGroupSymbols } from '@/features/markets/components/CommoditiesTable/useOtherGroupData';
import { useMarketAppControls } from '@/features/markets/components/contexts/MarketAppControlsContext';
import { useMarketWidget } from '@/features/markets/components/contexts/MarketWidgetContext';
import { MarketWidget } from '@/features/markets/components/MarketWidget';
import { ShareBlock } from '@/features/markets/components/Share/ShareBlock';
import TabExportButton, {
  isTabExportSupported
} from '@/features/markets/components/TabExportButton';
import WidgetButtons from '@/features/markets/components/WidgetButtons';
import { useBlockTabNavigation } from '@/features/markets/hooks/useBlockTabNavigation';
import { getMarketBlockTabParam } from '@/features/markets/lib/getMarketBlockTabParam';
import type { BaseTabType } from '@/features/markets/types';
import { ShareBlockNames } from '@/features/markets/types';
import {
  type ConflictingMode,
  MODE_CONFLICT_MESSAGES,
  SELECTOR_MODES
} from '@/features/symbol-selector';
import Tabs from '@/shared/ui/primitives/Tabs';
import TabPanel from '@/shared/ui/primitives/Tabs/TabPanel';
import { useAppSelector } from '@/store/hooks';

type TabsContainerProps = {
  tabs: BaseTabType[];
  layout?: { fullWidth?: boolean; height?: number };
  blockIndex: number;
};

const TabsContainer = ({ tabs = [], layout, blockIndex }: TabsContainerProps) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const { showShareBlock, showAddToDashboard } = useMarketAppControls();
  const { fullWidth } = layout ?? {};

  const selectorMode = useAppSelector(state => state.symbolSelector.selectorMode);
  const isModeIncompatible =
    selectorMode === SELECTOR_MODES.FORWARD_CURVE ||
    (tabs[activeTab]?.type === 'graph' && selectorMode === SELECTOR_MODES.SPOT);

  const handleModeConflictClick = () => {
    enqueueSnackbar(MODE_CONFLICT_MESSAGES[selectorMode as ConflictingMode], {
      variant: 'warning',
      autoHideDuration: 10000
    });
  };

  const { isFullScreen, setIsFullScreen, setFullScreenAvailable, fullScreenAvailable } =
    useMarketWidget();

  const isPersonalizationEnabled = useFlag(FeatureFlag.PersonalizationEnabled);

  const activeTabData = tabs[activeTab];
  const isIndexOnlyTable =
    activeTabData.type === 'table' && hasOtherGroupSymbols(activeTabData.sections);

  const { hasPermission } = usePermission();
  const isExportViewPermissionGranted = hasPermission(Permission.ExportView);
  const isFullScreenPermissionGranted = hasPermission(Permission.ViewChartInFullscreen);

  // Local editable tabs order for edit mode
  const visibleTabs = tabs;
  const blockName: ShareBlockNames = useMemo(() => {
    const tab = visibleTabs[activeTab];
    switch (tab.type) {
      case 'table':
        return ShareBlockNames.Table;
      case 'graph':
        if (tab.metadata?.graphType === 'custom') {
          return ShareBlockNames.CustomChart;
        }
        if (tab.metadata?.graphType === 'seasonal') {
          return ShareBlockNames.SeasonalChart;
        }
        if (tab.metadata?.graphType === 'forwardSimple') {
          return ShareBlockNames.ForwardCurveChart;
        }
        return ShareBlockNames.SpotChart;
      case 'feed':
        return ShareBlockNames.Feed;
      case 'minitable':
        return ShareBlockNames.Tables;
      case 'spot_history_table':
        return ShareBlockNames.Table;
      case 'csvtable':
        return ShareBlockNames.CSVTable;
      case 'file_list':
        return ShareBlockNames.Files;
      default:
        return ShareBlockNames.Table;
    }
  }, [activeTab, visibleTabs]);

  const isFullscreenAvailable = useCallback(
    (tabIndex: number) => {
      const tab = visibleTabs[tabIndex];
      let available = false;
      if (tab.type === 'graph') {
        const graphType = tab.metadata?.graphType;
        // Only dynamic/spot charts should allow fullscreen
        available = !['custom', 'seasonal', 'forwardSimple'].includes(graphType as string);
      }
      return available;
    },
    [visibleTabs]
  );

  // Update full screen availability as a side-effect based on the current tab
  useEffect(() => {
    setFullScreenAvailable(isFullscreenAvailable(activeTab));
  }, [activeTab, visibleTabs, setFullScreenAvailable, isFullscreenAvailable]);

  const shareTabParam = useMemo(
    () => getMarketBlockTabParam(blockName, blockIndex, visibleTabs[activeTab].name),
    [blockIndex, blockName, activeTab, visibleTabs]
  );

  const tabsWithDisabled = useMemo(
    () =>
      tabs.map((tab, index) => ({
        ...tab,
        disabled: isFullScreen && !isFullscreenAvailable(index)
      })),
    [tabs, isFullScreen, isFullscreenAvailable]
  );
  const canAddActiveTabToDashboard =
    activeTabData.type !== 'csvtable' &&
    activeTabData.type !== 'multi_group_table' &&
    activeTabData.metadata?.graphType !== 'custom' &&
    (activeTabData.type !== 'spot_history_table' || Boolean(activeTabData.items));

  const { blockRef } = useBlockTabNavigation(visibleTabs, setActiveTab);

  return (
    <Box
      ref={blockRef}
      component={Paper}
      sx={{
        ...(fullWidth ? { gridColumn: 'span 2' } : {}),
        ...(isFullScreen
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              zIndex: Z_INDEX.submenu,
              display: 'flex',
              flexDirection: 'column',
              // Allow inner panel to handle its own scrolling
              overflow: 'hidden',
              py: 2
            }
          : {})
      }}
    >
      <Box
        className='tabs__container'
        sx={{ height: 48, mb: 1.8, flexShrink: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            ...(isFullScreen && { px: 2 })
          }}
        >
          <Tabs
            activeTab={activeTab}
            height={48}
            scrollButtons='auto'
            tabs={tabsWithDisabled}
            onChange={setActiveTab}
          />
          <WidgetButtons
            buttons={[
              {
                id: 'add-to-dashboard',
                visible:
                  !isIndexOnlyTable &&
                  canAddActiveTabToDashboard &&
                  isPersonalizationEnabled &&
                  showAddToDashboard,
                locked: isModeIncompatible,
                overlay: <AddToDashboardButton tab={tabs[activeTab]} />
              },
              {
                id: 'share',
                visible: showShareBlock,
                locked: isModeIncompatible,
                overlay: (
                  <ShareBlock
                    activeTabName={tabs[activeTab].name}
                    blockName={blockName}
                    shareTabParam={shareTabParam}
                  />
                )
              },
              {
                id: 'export',
                visible: isExportViewPermissionGranted && isTabExportSupported(tabs[activeTab]),
                locked: isModeIncompatible,
                overlay: <TabExportButton tab={tabs[activeTab]} />
              },
              {
                id: 'fs-button',
                visible: isFullScreenPermissionGranted && (isFullScreen || fullScreenAvailable),
                icon: isFullScreen ? (
                  <CloseFullscreen sx={{ fontSize: 14 }} />
                ) : (
                  <OpenInFull sx={{ fontSize: 14 }} />
                ),
                title: isFullScreen ? 'Close full screen' : 'Full screen',
                onClick: () => {
                  setIsFullScreen(prev => !prev);
                }
              }
            ]}
            onLockedClick={handleModeConflictClick}
          />
        </Box>
      </Box>
      {visibleTabs.map((tab, index) => (
        <TabPanel
          key={tab.id}
          containerSX={{
            ...(isFullScreen
              ? {
                  // occupy remaining space below the tabs header and respect parent padding
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  px: 2
                }
              : {})
          }}
          index={index}
          value={activeTab}
        >
          <MarketWidget tab={tab} />
        </TabPanel>
      ))}
    </Box>
  );
};
export default TabsContainer;
